import { useCallback, useEffect, useRef, useState } from 'react';

export type EditorMode = 'gui' | 'json';

export interface SwitchResult {
  ok: boolean;
  error?: string;
}

interface UseJsonGuiToggleOptions<T> {
  /** Canonical value (typically the catalog). */
  value: T;
  /** Called when JSON mode commits a parsed value back. */
  onChange: (next: T) => void;
  /** Initial mode (defaults to 'gui'). */
  initialMode?: EditorMode;
  /**
   * Optional validator run on parsed JSON before committing. Return an array of
   * error messages — empty means valid. If a parsed payload is invalid, the
   * mode switch is rejected with the first message.
   */
  validate?: (parsed: unknown) => string[];
}

interface UseJsonGuiToggleResult {
  mode: EditorMode;
  jsonText: string;
  setJsonText: (text: string) => void;
  /**
   * Switch modes. On gui → json, serializes the current value to text.
   * On json → gui, parses+validates the current text and commits via onChange.
   * Returns `{ ok: false, error }` if the switch was rejected.
   */
  switchMode: (target: EditorMode) => SwitchResult;
  /**
   * Re-sync the JSON text from the canonical value (e.g. after a save returns
   * normalized data). No-op if already in json mode and text is dirty —
   * callers should decide when to overwrite.
   */
  syncJsonFromValue: () => void;
}

/**
 * Manages a GUI/JSON toggle for catalog-style admin editors. The canonical
 * source of truth is the upstream `value` (controlled by the parent). JSON
 * text lives inside the hook and is committed back via `onChange` when
 * switching from json → gui.
 */
export function useJsonGuiToggle<T>(
  options: UseJsonGuiToggleOptions<T>,
): UseJsonGuiToggleResult {
  const { value, onChange, initialMode = 'gui', validate } = options;
  const [mode, setMode] = useState<EditorMode>(initialMode);
  const [jsonText, setJsonText] = useState<string>(() => JSON.stringify(value, null, 2));

  // Keep the latest onChange/validate without making switchMode reactive to
  // them — the hook's API stability matters more than re-renders here.
  const onChangeRef = useRef(onChange);
  const validateRef = useRef(validate);
  useEffect(() => {
    onChangeRef.current = onChange;
    validateRef.current = validate;
  }, [onChange, validate]);

  const syncJsonFromValue = useCallback(() => {
    setJsonText(JSON.stringify(value, null, 2));
  }, [value]);

  const switchMode = useCallback(
    (target: EditorMode): SwitchResult => {
      if (target === mode) return { ok: true };

      if (target === 'json') {
        setJsonText(JSON.stringify(value, null, 2));
        setMode('json');
        return { ok: true };
      }

      // json → gui: parse + validate
      let parsed: unknown;
      try {
        parsed = JSON.parse(jsonText);
      } catch {
        return { ok: false, error: 'Cannot switch to GUI: the JSON is invalid. Fix the JSON first.' };
      }

      const errors = validateRef.current?.(parsed) ?? [];
      if (errors.length > 0) {
        return { ok: false, error: errors[0] };
      }

      onChangeRef.current(parsed as T);
      setMode('gui');
      return { ok: true };
    },
    [mode, value, jsonText],
  );

  return { mode, jsonText, setJsonText, switchMode, syncJsonFromValue };
}
